import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { ServiceModeType, ServiceModeConfig, SidecarStatusData } from '@aigatewayx/shared/types/service-mode';
import { DEFAULT_SERVICE_MODE_CONFIG, isTauri } from '@aigatewayx/shared/types/service-mode';

export const useServiceModeStore = defineStore('service-mode', () => {
  const mode = ref<ServiceModeType>(DEFAULT_SERVICE_MODE_CONFIG.mode);
  const serverAddress = ref(DEFAULT_SERVICE_MODE_CONFIG.serverAddress);
  const localPort = ref(DEFAULT_SERVICE_MODE_CONFIG.localPort);
  const sidecarStatus = ref<string>('idle');
  const sidecarError = ref<string>('');
  const sidecarPid = ref<number | null>(null);
  const connectionVerified = ref(false);
  const connectionLatency = ref<number | null>(null);
  const isSwitching = ref(false);
  const switchDirection = ref<'to-local' | 'to-remote' | null>(null);
  const switchProgress = ref('');
  const switchError = ref('');

  const currentApiBase = computed(() => {
    if (mode.value === 'local') {
      return `http://localhost:${localPort.value}`;
    }
    return serverAddress.value;
  });

  const isSidecarReady = computed(() => sidecarStatus.value === 'running');
  const isLocalMode = computed(() => mode.value === 'local');
  const isRemoteMode = computed(() => mode.value === 'remote');

  async function loadConfig(): Promise<ServiceModeConfig> {
    if (!isTauri()) return DEFAULT_SERVICE_MODE_CONFIG;
    try {
      const { load } = await import('@tauri-apps/plugin-store');
      const store = await load('service-mode.json', { autoSave: false, defaults: {} });
      const saved = await store.get<ServiceModeConfig>('config');
      if (saved) {
        mode.value = saved.mode;
        serverAddress.value = saved.serverAddress;
        localPort.value = saved.localPort;
      }
      return saved || DEFAULT_SERVICE_MODE_CONFIG;
    } catch {
      return DEFAULT_SERVICE_MODE_CONFIG;
    }
  }

  async function saveConfig(): Promise<void> {
    if (!isTauri()) return;
    try {
      const { load } = await import('@tauri-apps/plugin-store');
      const store = await load('service-mode.json', { autoSave: false, defaults: {} });
      const config: ServiceModeConfig = {
        mode: mode.value,
        serverAddress: serverAddress.value,
        localPort: localPort.value,
        lastUpdated: new Date().toISOString(),
      };
      await store.set('config', config);
      await store.save();
    } catch (e) {
      console.error('Failed to save service mode config:', e);
    }
  }

  async function confirmLocalMode(): Promise<boolean> {
    mode.value = 'local';
    sidecarStatus.value = 'starting';
    sidecarError.value = '';
    if (isTauri()) {
      // Check if sidecar is already running
      try {
        const res = await fetch(`http://localhost:${localPort.value}/api/health`);
        if (res.ok) {
          sidecarStatus.value = 'running';
          await saveConfig();
          return true;
        }
      } catch { /* not running yet, proceed to start */ }

      try {
        const { invoke } = await import('@tauri-apps/api/core');
        await invoke('start_sidecar', { port: localPort.value });
        // Wait for server to be ready
        for (let i = 0; i < 10; i++) {
          await new Promise(r => setTimeout(r, 500));
          try {
            const res = await fetch(`http://localhost:${localPort.value}/api/health`);
            if (res.ok) {
              sidecarStatus.value = 'running';
              break;
            }
          } catch {}
        }
        if (sidecarStatus.value !== 'running') {
          sidecarError.value = '服务启动超时，请检查端口是否被占用或服务是否崩溃';
          sidecarStatus.value = 'error';
          await saveConfig();
          return false;
        }
      } catch (e) {
        sidecarError.value = String(e);
        sidecarStatus.value = 'error';
        await saveConfig();
        return false;
      }
    } else {
      // Browser mode: no sidecar needed, API calls use Vite proxy
      sidecarStatus.value = 'running';
    }
    await saveConfig();
    // Redirect to backend-served frontend for same-origin API access
    if (isTauri() && sidecarStatus.value === 'running') {
      window.location.href = `http://localhost:${localPort.value}`;
    }
    return true;
  }

  async function confirmRemoteMode(): Promise<void> {
    mode.value = 'remote';
    sidecarStatus.value = 'idle';
    sidecarPid.value = null;
    sidecarError.value = '';
    await saveConfig();
    // Open remote server in the default system browser (handles CORS correctly)
    if (isTauri() && serverAddress.value) {
      try {
        const { open } = await import('@tauri-apps/plugin-shell');
        await open(serverAddress.value);
      } catch (e) {
        console.warn('Failed to open browser:', e);
      }
    }
  }

  async function switchToRemote(): Promise<boolean> {
    if (!isTauri()) {
      mode.value = 'remote';
      await saveConfig();
      return true;
    }
    isSwitching.value = true;
    switchDirection.value = 'to-remote';
    switchProgress.value = '正在停止本地服务...';
    switchError.value = '';
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke<string>('switch_service_mode', { targetMode: 'remote' });
      mode.value = 'remote';
      sidecarStatus.value = 'idle';
      sidecarPid.value = null;
      switchProgress.value = '已切换至远程模式';
      await saveConfig();
      await verifyConnection();
      return true;
    } catch (e) {
      switchError.value = String(e);
      switchProgress.value = '切换失败';
      return false;
    } finally {
      isSwitching.value = false;
      switchDirection.value = null;
    }
  }

  async function switchToLocal(): Promise<boolean> {
    if (!isTauri()) {
      mode.value = 'local';
      await saveConfig();
      return true;
    }
    isSwitching.value = true;
    switchDirection.value = 'to-local';
    switchProgress.value = '正在启动本地服务...';
    switchError.value = '';
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke<string>('switch_service_mode', { targetMode: 'local' });
      mode.value = 'local';
      switchProgress.value = '等待本地服务就绪...';
      await waitForSidecarReady(30000);
      await saveConfig();
      await verifyConnection();
      return true;
    } catch (e) {
      switchError.value = String(e);
      switchProgress.value = '切换失败，已回退至远程模式';
      mode.value = 'remote';
      return false;
    } finally {
      isSwitching.value = false;
      switchDirection.value = null;
    }
  }

  async function verifyConnection(): Promise<boolean> {
    const base = currentApiBase.value;
    try {
      const start = Date.now();
      const res = await fetch(`${base}/api/health`, { method: 'GET', signal: AbortSignal.timeout(5000) });
      connectionLatency.value = Date.now() - start;
      connectionVerified.value = res.ok;
      return res.ok;
    } catch {
      connectionVerified.value = false;
      connectionLatency.value = null;
      return false;
    }
  }

  async function waitForSidecarReady(timeoutMs: number): Promise<boolean> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (sidecarStatus.value === 'running') return true;
      await new Promise((r) => setTimeout(r, 500));
    }
    return false;
  }

  async function listenSidecarEvents(): Promise<(() => void) | null> {
    if (!isTauri()) return null;
    try {
      const { listen } = await import('@tauri-apps/api/event');
      const unlistenStatus = await listen<SidecarStatusData>('sidecar:status-changed', (event) => {
        const s = event.payload;
        sidecarStatus.value = s.status;
        if (s.status === 'running' && s.data?.pid) {
          sidecarPid.value = s.data.pid;
        }
        if (s.status === 'error' && s.data?.message) {
          sidecarError.value = s.data.message;
        }
        if (s.status === 'idle' || s.status === 'stopped') {
          sidecarPid.value = null;
        }
      });
      const unlistenStderr = await listen<string>('sidecar:stderr', (event) => {
        console.warn('[sidecar:stderr]', event.payload);
      });
      return () => {
        unlistenStatus();
        unlistenStderr();
      };
    } catch {
      return null;
    }
  }

  return {
    mode, serverAddress, localPort, sidecarStatus, sidecarError, sidecarPid,
    connectionVerified, connectionLatency,
    isSwitching, switchDirection, switchProgress, switchError,
    currentApiBase, isSidecarReady, isLocalMode, isRemoteMode,
    loadConfig, saveConfig,
    confirmLocalMode, confirmRemoteMode,
    switchToRemote, switchToLocal,
    verifyConnection, waitForSidecarReady, listenSidecarEvents,
  };
});
